import { useState } from 'react';
import { Set } from 'immutable';
import { EnrollmentDropdown } from '../../components/Enrollment';
import { ComparableEnrollment } from '../../model';
import { Button, Flex, FlexStack, Grid, GridArea, Text } from '../../components';
import { css } from '@emotion/css';
import useTheme from '../../components/Theme/useTheme';
import { Stack } from '@mui/material';

interface CountyReportFormProps {
  onMemberTypeChange: (memberTypeIds: string | null) => void;
  onCountyChange: (countyIds: string[]) => void;
  onEnrollmentChange: (enrollmentIds: string[]) => void;
}

function CountyReportForm({
  onMemberTypeChange,
  onCountyChange,
  onEnrollmentChange,
}: CountyReportFormProps) {
  const [memberTypeEnrollments, setMemberTypeEnrollments] = useState<Set<ComparableEnrollment>>(
    Set()
  );
  const [countyEnrollments, setCountyEnrollments] = useState<Set<ComparableEnrollment>>(Set());
  const [generalEnrollments, setGeneralEnrollments] = useState<Set<ComparableEnrollment>>(Set());

  const handleMemberTypeFinished = async (selected: Set<ComparableEnrollment>) => {
    setMemberTypeEnrollments(selected);
    onMemberTypeChange(selected.map(e => e.id!).first());
  };

  const handleCountyFinished = async (selected: Set<ComparableEnrollment>) => {
    setCountyEnrollments(selected);
    onCountyChange(
      selected
        .filter(x => typeof x?.id === 'string')
        .map(e => e.id!)
        .toArray()
    );
  };

  const handleEnrollmentFinished = async (selected: Set<ComparableEnrollment>) => {
    setGeneralEnrollments(selected);
    onEnrollmentChange(
      selected
        .filter(x => typeof x?.id === 'string')
        .map(e => e.id!)
        .toArray()
    );
  };

  const theme = useTheme();

  const SelectedEnrollmentItem = ({
    enrollment,
    onRemove,
  }: {
    enrollment: ComparableEnrollment;
    onRemove: () => void;
  }) => {
    return (
      <FlexStack
        className={css({
          marginBottom: theme.spacing.sm,
          alignItems: 'flex-start',
        })}
      >
        <span>{enrollment.name}</span>
        <Button link onClick={onRemove}>
          Remove
        </Button>
      </FlexStack>
    );
  };

  const handleRemoveMemberType = (enrollment: ComparableEnrollment) => {
    setMemberTypeEnrollments(Set());
    onMemberTypeChange(null);
  };

  const handleRemoveCounty = (enrollment: ComparableEnrollment) => {
    setCountyEnrollments(countyEnrollments.remove(enrollment));
    onCountyChange(
      countyEnrollments
        .remove(enrollment)
        .filter(x => typeof x?.id === 'string')
        .map(e => e.id!)
        .toArray()
    );
  };

  const handleRemoveEnrollment = (enrollment: ComparableEnrollment) => {
    setGeneralEnrollments(generalEnrollments.remove(enrollment));
    onEnrollmentChange(
      generalEnrollments
        .remove(enrollment)
        .filter(x => typeof x?.id === 'string')
        .map(e => e.id!)
        .toArray()
    );
  };

  return (
    <>
      <Text className={css({ marginBottom: theme.spacing.md })}>
        <p>To generate a county report, please follow these steps:</p>
        <ol>
          <li>
            Select a <strong>Member Type</strong> (required)
          </li>
          <li>
            Select one or more <strong>Counties</strong> (required)
          </li>
          <li>
            Optionally select <strong>Excluded Enrollments</strong>
          </li>
        </ol>
        <p>
          Once you've set these parameters, click the "Generate & Download" button to download the
          report.
        </p>
      </Text>
      <Grid
        template={`
        "memberType county"
        "enrollment ."
        / 1fr 1fr`}
        gap={32}
      >
        <GridArea area="memberType">
          <Stack>
            <EnrollmentDropdown
              label="Member Type"
              selected={memberTypeEnrollments}
              type="MemberType"
              onFinished={handleMemberTypeFinished}
            />
            {memberTypeEnrollments
              .map(enrollment => (
                <SelectedEnrollmentItem
                  key={enrollment.id}
                  enrollment={enrollment}
                  onRemove={() => handleRemoveMemberType(enrollment)}
                />
              ))
              .toArray()}
            {memberTypeEnrollments.size === 0 && (
              <Flex>
                <span>No member type selected</span>
              </Flex>
            )}
          </Stack>
        </GridArea>
        <GridArea area="county">
          <Stack>
            <EnrollmentDropdown
              label="County"
              selected={countyEnrollments}
              multiple={true}
              type="County"
              onFinished={handleCountyFinished}
            />
          </Stack>
          {countyEnrollments
            .map(enrollment => (
              <SelectedEnrollmentItem
                key={enrollment.id}
                enrollment={enrollment}
                onRemove={() => handleRemoveCounty(enrollment)}
              />
            ))
            .toArray()}
          {countyEnrollments.size === 0 && (
            <Flex>
              <span>No counties selected</span>
            </Flex>
          )}
        </GridArea>
        <GridArea area="enrollment">
          <Stack>
            <EnrollmentDropdown
              label="Excluded Enrollments"
              selected={generalEnrollments}
              multiple={true}
              type="MemberType"
              onFinished={handleEnrollmentFinished}
            />
            {generalEnrollments
              .map(enrollment => (
                <SelectedEnrollmentItem
                  key={enrollment.id}
                  enrollment={enrollment}
                  onRemove={() => handleRemoveEnrollment(enrollment)}
                />
              ))
              .toArray()}
            {generalEnrollments.size === 0 && (
              <Flex>
                <span>No enrollments selected</span>
              </Flex>
            )}
          </Stack>
        </GridArea>
      </Grid>
    </>
  );
}

export default CountyReportForm;
